<script setup>
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { MESSAGE_TYPES } from 'dashboard/components-next/message/constants';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
  messages: {
    type: Array,
    default: () => [],
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const route = useRoute();
const actionState = reactive({});

const statusLabels = computed(() => ({
  PENDING: t('AI_RECOMMENDATIONS.STATUS.PENDING'),
  ACCEPTED: t('AI_RECOMMENDATIONS.STATUS.ACCEPTED'),
  REJECTED: t('AI_RECOMMENDATIONS.STATUS.REJECTED'),
}));

const contactName = computed(
  () =>
    props.contact?.name ||
    props.contact?.email ||
    props.contact?.phoneNumber ||
    t('AI_RECOMMENDATIONS.CONTACT_FALLBACK')
);

const sourceMessage = computed(() => {
  const incomingMessages = props.messages.filter(
    message => message.messageType === MESSAGE_TYPES.INCOMING
  );
  const productMessage = incomingMessages.find(message =>
    (message.content || '').toLowerCase().match(/bague|ring|violet/)
  );

  return (
    productMessage ||
    incomingMessages.slice(-1)[0] ||
    props.messages.slice(-1)[0]
  );
});

const sourceRoute = computed(() => {
  if (!sourceMessage.value?.id) return null;

  return {
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: props.conversationId,
    },
    query: { messageId: sourceMessage.value.id },
  };
});

const recommendations = computed(() => [
  {
    id: 'preference_update',
    icon: 'i-lucide-sliders-horizontal',
    title: t('AI_RECOMMENDATIONS.ITEMS.PREFERENCE_UPDATE.TITLE'),
    description: t('AI_RECOMMENDATIONS.ITEMS.PREFERENCE_UPDATE.DESCRIPTION', {
      name: contactName.value,
    }),
    action: t('AI_RECOMMENDATIONS.ITEMS.PREFERENCE_UPDATE.ACTION'),
  },
  {
    id: 'create_task',
    icon: 'i-lucide-list-plus',
    title: t('AI_RECOMMENDATIONS.ITEMS.CREATE_TASK.TITLE'),
    description: t('AI_RECOMMENDATIONS.ITEMS.CREATE_TASK.DESCRIPTION'),
    action: t('AI_RECOMMENDATIONS.ITEMS.CREATE_TASK.ACTION'),
    sourceRoute: sourceRoute.value,
    sourceLabel: t('AI_RECOMMENDATIONS.ITEMS.CREATE_TASK.SOURCE_LABEL'),
  },
  {
    id: 'close_circle',
    icon: 'i-lucide-users-round',
    title: t('AI_RECOMMENDATIONS.ITEMS.CLOSE_CIRCLE.TITLE'),
    description: t('AI_RECOMMENDATIONS.ITEMS.CLOSE_CIRCLE.DESCRIPTION'),
    action: t('AI_RECOMMENDATIONS.ITEMS.CLOSE_CIRCLE.ACTION'),
  },
]);

const visibleRecommendations = computed(() =>
  props.compact ? recommendations.value.slice(0, 2) : recommendations.value
);

const setStatus = (id, status) => {
  actionState[id] = status;
};
</script>

<template>
  <div
    class="flex flex-col gap-2 rounded-xl border border-n-violet-6 bg-n-violet-2 p-3 shadow-sm"
  >
    <div class="flex items-center justify-between gap-3">
      <div class="flex min-w-0 items-center gap-2">
        <span
          class="flex size-6 shrink-0 items-center justify-center rounded-lg bg-n-violet-9 text-white"
        >
          <span class="i-lucide-sparkles size-3.5" />
        </span>
        <h3 class="mb-0 truncate text-sm font-semibold text-n-violet-12">
          {{ t('AI_RECOMMENDATIONS.TITLE') }}
        </h3>
      </div>
      <span
        class="shrink-0 rounded-full bg-white/60 px-2 py-0.5 text-[11px] font-semibold text-n-violet-11 dark:bg-n-violet-3"
      >
        {{ t('AI_RECOMMENDATIONS.MOCK_BADGE') }}
      </span>
    </div>

    <div
      v-for="recommendation in visibleRecommendations"
      :key="recommendation.id"
      class="rounded-lg border border-n-violet-5 bg-white/70 p-3 dark:bg-n-violet-2/80"
    >
      <div class="min-w-0">
        <h4 class="mb-1 text-sm font-semibold text-n-slate-12">
          {{ recommendation.title }}
        </h4>
        <p class="mb-0 text-sm leading-5 text-n-slate-11">
          {{ recommendation.description }}
          <RouterLink
            v-if="recommendation.sourceRoute"
            :to="recommendation.sourceRoute"
            class="font-semibold underline underline-offset-2 text-n-violet-11"
          >
            {{ recommendation.sourceLabel }}
          </RouterLink>
        </p>
        <p v-if="!compact" class="mb-0 mt-1 text-xs text-n-slate-10">
          {{ recommendation.action }}
        </p>
      </div>

      <div class="mt-3 flex items-center justify-between gap-2">
        <span class="text-xs font-medium text-n-slate-10">
          {{
            statusLabels[actionState[recommendation.id]] || statusLabels.PENDING
          }}
        </span>
        <div class="flex shrink-0 items-center gap-2">
          <button
            type="button"
            class="rounded-lg px-2.5 py-1 text-xs font-semibold text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="setStatus(recommendation.id, 'REJECTED')"
          >
            {{ t('AI_RECOMMENDATIONS.ACTIONS.REJECT') }}
          </button>
          <button
            type="button"
            class="rounded-lg bg-n-violet-9 px-2.5 py-1 text-xs font-semibold text-white transition hover:bg-n-violet-10"
            @click="setStatus(recommendation.id, 'ACCEPTED')"
          >
            {{ t('AI_RECOMMENDATIONS.ACTIONS.ACCEPT') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
